import { useState } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { CallAnalysis } from "../../../api/generated";
import { SCORING_RUBRIC, getScoreColor, getTier } from "../../../utils/scoringRubric";

interface ScoreSummaryProps {
  analysis: CallAnalysis;
}

function ScoreBar({ label, score }: { label: string; score: number | null }) {
  if (score == null) {
    return (
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
          <Typography variant="body2">{label}</Typography>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            N/A
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={0}
          sx={{ height: 6, borderRadius: 3, opacity: 0.3 }}
        />
      </Box>
    );
  }

  const color = getScoreColor(score);

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" color={`${color}.main`} sx={{
          fontWeight: 600
        }}>
          {score}/5
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={score * 20}
        color={color === "inherit" ? "primary" : color}
        sx={{ height: 6, borderRadius: 3 }}
      />
    </Box>
  );
}

export function ScoreSummary({ analysis }: ScoreSummaryProps) {
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleAccordionChange =
    (panel: string) => (_: unknown, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const tier =
    analysis.overall_score != null ? getTier(analysis.overall_score) : null;

  return (
    <Box>
      {/* Overall Score */}
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Box sx={{ position: "relative", display: "inline-flex" }}>
          <CircularProgress
            variant="determinate"
            value={analysis.overall_score ?? 0}
            size={100}
            thickness={4}
            color={tier?.color ?? "inherit"}
          />
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="h4" sx={{
              fontWeight: 700
            }}>
              {analysis.overall_score ?? "—"}
            </Typography>
          </Box>
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            mt: 1
          }}>
          Overall Score
        </Typography>
        {tier && (
          <>
            <Typography
              variant="body2"
              color={`${tier.color}.main`}
              sx={{
                fontWeight: 600,
                mt: 0.5
              }}>
              {tier.emoji} {tier.name}
            </Typography>
            <Typography variant="caption" sx={{
              color: "text.secondary"
            }}>
              {tier.description}
            </Typography>
          </>
        )}
      </Box>
      {/* Category Accordions */}
      {SCORING_RUBRIC.map((category) => {
        const catScore = analysis[category.scoreField as keyof CallAnalysis] as
          | number
          | null;

        return (
          <Accordion
            key={category.key}
            expanded={expanded === category.key}
            onChange={handleAccordionChange(category.key)}
            disableGutters
            sx={{ "&:before": { display: "none" }, boxShadow: "none" }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                  pr: 1,
                }}
              >
                <Typography variant="subtitle2">{category.label}</Typography>
                <Typography
                  variant="subtitle2"
                  color={
                    catScore != null
                      ? `${getScoreColor(catScore)}.main`
                      : "text.secondary"
                  }
                >
                  {catScore != null ? `${catScore}/5` : "N/A"}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {category.dimensions.map((dim) => {
                const dimScore = analysis[dim.field as keyof CallAnalysis] as
                  | number
                  | null;
                return (
                  <ScoreBar
                    key={dim.field}
                    label={dim.label}
                    score={dimScore}
                  />
                );
              })}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}
